import { ClientItem } from 'owa-mail-store';
import getItem from 'owa-mail-store/lib/services/getItem';
import checkIfResponseSuccess from 'owa-mail-store/lib/services/utils/checkIfResponseSuccess';
import ExtendedPropertyType from 'owa-service/lib/contract/ExtendedPropertyType';
import ExtendedPropertyUri from 'owa-service/lib/contract/ExtendedPropertyUri';
import Item from 'owa-service/lib/contract/Item';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import extendedPropertyUri from 'owa-service/lib/factory/extendedPropertyUri';
import itemResponseShape from 'owa-service/lib/factory/itemResponseShape';
import { trace } from 'owa-trace';

const SUBSTRATE_SEARCH_SUGGESTIONS_PROPERTY_NAME = 'EntityExtraction/SubstrateSearchSuggestions';
const REPLY_WITH_QUERY_COVERAGE_PROPERTY_NAME = 'EntityExtraction/ReplyWithQueryCoverage';
const DISTINGUISHED_PROPERTY_SET_ID = 'Common';
const PROPERTY_TYPE = 'String';

const substrateSearchSuggestionsExtendedPropertyUri: ExtendedPropertyUri = extendedPropertyUri({
    PropertyName: SUBSTRATE_SEARCH_SUGGESTIONS_PROPERTY_NAME,
    DistinguishedPropertySetId: DISTINGUISHED_PROPERTY_SET_ID,
    PropertyType: PROPERTY_TYPE,
});

const replyWithQueryCoverageExtendedPropertyUri: ExtendedPropertyUri = extendedPropertyUri({
    PropertyName: REPLY_WITH_QUERY_COVERAGE_PROPERTY_NAME,
    DistinguishedPropertySetId: DISTINGUISHED_PROPERTY_SET_ID,
    PropertyType: PROPERTY_TYPE,
});

/**
 * SubstrateSearchSuggestions property contains info about sentences to highlight and how to build the 3S call for the sentence.
 * ReplyWithQueryCoverage tells us which sentences 3S will actually provide suggestions for.
 */
export interface ReplyWithProperties {
    SubstrateSearchSuggestions: ExtendedPropertyType;
    ReplyWithQueryCoverage: ExtendedPropertyType;
}

export async function getReplyWithProperties(item: ClientItem): Promise<ReplyWithProperties> {
    let extendedProperties: ReplyWithProperties = getReplyWithPropertiesOnItem(item);

    if (
        extendedProperties.SubstrateSearchSuggestions == null &&
        extendedProperties.ReplyWithQueryCoverage == null
    ) {
        const itemShape = itemResponseShape({
            BaseShape: 'IdOnly',
            AdditionalProperties: [
                substrateSearchSuggestionsExtendedPropertyUri,
                replyWithQueryCoverageExtendedPropertyUri,
            ],
        });

        const response = await getItem(
            [item.ItemId.Id],
            itemShape,
            null /* shapeName */,
            null /* RequestServerVersion */,
            item.MailboxInfo?.requestOptions
        );

        if (checkIfResponseSuccess(response[0])) {
            const responseItem = response[0] as ItemInfoResponseMessage;
            if (responseItem?.Items?.[0]) {
                const returnedItem = responseItem.Items[0];
                extendedProperties = getReplyWithPropertiesOnItem(returnedItem);
            } else {
                trace.info('getReplyWithProperties: Unable to parse GetItem response properly.');
            }
        }
    }

    return extendedProperties;
}

function getReplyWithPropertiesOnItem(item: Item): ReplyWithProperties {
    const properties: ReplyWithProperties = {
        SubstrateSearchSuggestions: null,
        ReplyWithQueryCoverage: null,
    };

    const extendedProperties = item.ExtendedProperty;
    if (extendedProperties) {
        const numProperties = extendedProperties.length;
        for (let i = 0; i < numProperties; i++) {
            const prop = extendedProperties[i];
            if (prop.ExtendedFieldURI.PropertyName === SUBSTRATE_SEARCH_SUGGESTIONS_PROPERTY_NAME) {
                properties.SubstrateSearchSuggestions = prop;
            } else if (
                prop.ExtendedFieldURI.PropertyName === REPLY_WITH_QUERY_COVERAGE_PROPERTY_NAME
            ) {
                properties.ReplyWithQueryCoverage = prop;
            }
        }
    }

    return properties;
}
